import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { LayoutTemplate, Plus } from "@/lib/icons";
import { useState } from "react";
import { toast } from "sonner";
import { errorWords } from "@alm/domain";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { ToneBar } from "@/components/app/sidebar/tone-bar";
import { Accordion } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import { orpc, orpcClient, type Output } from "@/lib/orpc";
import { optionalOneOf, optionalString, KEEP_PLACE, optionalSearch } from "@/lib/search";
import { useCan } from "@/lib/policy";
import { ApplyChecklistTemplateDialog } from "@/routes/_authenticated/_care/residents/$residentId/-components/stay/apply-checklist-template-dialog";
import { AssignChecklistItemDialog } from "./-components/stay/checklist-dialogs";
import {
  ChecklistEntryDialog,
  type ChecklistStatusChange,
} from "@/routes/_authenticated/_care/residents/$residentId/-components/stay/checklist-entry-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { ChecklistFilters, sortEntries, type Sort } from "./-components/stay/checklist-filters";
import { ProfileSection } from "./-components/chart/profile-section";
import { SectionEmpty } from "@/routes/_authenticated/_care/residents/$residentId/-components/chart/section-empty";
import { RemoveChecklistEntryDialog } from "@/routes/_authenticated/_care/residents/$residentId/-components/stay/remove-checklist-entry-dialog";
import { ChecklistEntryItem } from "./-components/stay/checklist-entry-item";

const SORTS = [
  "title-asc",
  "title-desc",
  "due-asc",
  "due-desc",
  "category",
] as const satisfies readonly Sort[];
type ChecklistSearch = { q?: string; sort?: Sort; status?: string };

export const Route = createFileRoute("/_authenticated/_care/residents/$residentId/checklists")({
  validateSearch: (search: Record<string, unknown>): ChecklistSearch => ({
    q: optionalSearch(search.q),
    sort: optionalOneOf(SORTS, search.sort),
    status: optionalString(search.status)?.slice(0, 40) || undefined,
  }),
  component: ChecklistsTab,
});

type Entry = Output<typeof orpcClient.care.checklists.forResident>[number];

/** What this resident still has to have done, and what has been. */
function ChecklistsTab() {
  const { residentId } = Route.useParams();
  const { data: resident } = useSuspenseQuery(
    orpc.care.residents.get.queryOptions({ input: { residentId } }),
  );
  const tz = resident.facilityTimezone ?? "UTC";
  const canWrite = useCan("resident.profile:update", resident.facilityId);
  const queryClient = useQueryClient();
  const scope = resident.facilityId ?? undefined;
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const patch = (next: Partial<ChecklistSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...next }), ...KEEP_PLACE });
  const { text: search, change: setSearch } = useSearchParamText(params.q, (q) => patch({ q }));
  const sort: Sort = params.sort ?? "default";
  const setSort = (next: Sort) => patch({ sort: optionalOneOf(SORTS, next) });
  const status = params.status ?? "all";
  const setStatus = (next: string) => patch({ status: next === "all" ? undefined : next });
  const [dialog, setDialog] = useState<"assign" | "template" | null>(null);
  const [changing, setChanging] = useState<{
    entry: Entry;
    status: ChecklistStatusChange | null;
  } | null>(null);
  const [removing, setRemoving] = useState<Entry | null>(null);

  const entries = useQuery(
    orpc.care.checklists.forResident.queryOptions({ input: { residentId } }),
  );
  const items = useQuery(
    orpc.care.checklists.items.list.queryOptions({
      input: { facilityId: scope, includeInactive: false },
    }),
  );
  const templates = useQuery(
    orpc.care.checklists.templates.list.queryOptions({ input: { facilityId: scope } }),
  );
  const remove = useMutation(
    orpc.care.checklists.remove.mutationOptions({
      onSuccess: async () => {
        toast.success("Checklist item removed");
        setRemoving(null);
        await queryClient.invalidateQueries({ queryKey: orpc.care.checklists.key() });
      },
      onError: (error) => toast.error(errorWords(error)),
    }),
  );

  const all = entries.data ?? [];
  const total = all.length;
  const completed = all.filter((row) => row.status === "completed").length;
  const pending = all.filter((row) => row.status === "pending").length;
  const needle = search.trim().toLowerCase();
  const shown = sortEntries(
    all.filter(
      (row) =>
        (status === "all" || row.status === status) &&
        (!needle ||
          (row.title ?? "").toLowerCase().includes(needle) ||
          (row.description ?? "").toLowerCase().includes(needle) ||
          (row.category ?? "").toLowerCase().includes(needle)),
    ),
    sort,
  );
  const unassigned = (items.data ?? []).filter(
    (item) => !all.some((row) => row.checklistItemId === item.id),
  );

  return (
    <ProfileSection
      title="Checklists"
      description={`${completed} of ${total} complete`}
      action={
        canWrite ? (
          <>
            <Button
              size="sm"
              variant="outline"
              disabled={unassigned.length === 0}
              onClick={() => setDialog("assign")}
            >
              <Plus className="size-4" />
              Assign item
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={!templates.data?.length}
              onClick={() => setDialog("template")}
            >
              <LayoutTemplate className="size-4" />
              Apply template
            </Button>
          </>
        ) : null
      }
    >
      <div className="flex flex-col gap-5">
        {/* Tiles and filters wait for something to count and filter. */}
        {total > 0 ? (
          <>
            {/* One bar of the whole, the count inside each fill: three KPI
                cards in the card said the same six items three times. */}
            <ToneBar
              label="Checklist progress"
              segments={[
                { key: "completed", label: "Completed", count: completed, tone: "ok" },
                { key: "pending", label: "Pending", count: pending, tone: "warn" },
                {
                  key: "waived",
                  label: "Waived",
                  count: total - completed - pending,
                  tone: "idle",
                },
              ]}
            />
            <ChecklistFilters
              search={search}
              onSearch={setSearch}
              sort={sort}
              onSort={setSort}
              status={status}
              onStatus={setStatus}
            />
            <p className="text-muted-foreground text-xs">
              {shown.length === total
                ? `${total} ${total === 1 ? "item" : "items"}`
                : `${shown.length} of ${total} items, filtered`}
            </p>
          </>
        ) : null}
        {entries.isPending ? (
          <Skeleton className="h-24 w-full" />
        ) : entries.isError ? (
          <ReadFailed
            message="Could not load this resident's checklist."
            error={entries.error}
            onRetry={() => void entries.refetch()}
            retrying={entries.isFetching}
          />
        ) : shown.length === 0 ? (
          <SectionEmpty
            title={total > 0 ? "No matching checklist items" : "No checklist items assigned"}
            detail={
              total > 0
                ? "Widen the search or change the status filter to see more."
                : unassigned.length === 0 && !templates.data?.length
                  ? "No checklist items or templates exist yet. Create them in Settings, then assign them here."
                  : "Assign an item or apply a template to get started."
            }
            action={
              canWrite && total === 0 && unassigned.length === 0 && !templates.data?.length ? (
                <Button variant="outline" asChild>
                  <Link to="/settings/checklists">Open checklist settings</Link>
                </Button>
              ) : undefined
            }
          />
        ) : (
          <Accordion type="multiple" className="w-full">
            {shown.map((entry) => (
              <ChecklistEntryItem
                key={entry.id}
                entry={entry}
                tz={tz}
                canWrite={canWrite}
                onChange={(next) => setChanging({ entry, status: next })}
                onRemove={() => setRemoving(entry)}
              />
            ))}
          </Accordion>
        )}
      </div>

      {dialog === "assign" ? (
        <AssignChecklistItemDialog
          residentId={residentId}
          items={unassigned}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog === "template" ? (
        <ApplyChecklistTemplateDialog
          residentId={residentId}
          facilityId={scope}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {changing ? (
        <ChecklistEntryDialog
          entry={changing.entry}
          status={changing.status}
          onClose={() => setChanging(null)}
        />
      ) : null}
      <RemoveChecklistEntryDialog
        entry={removing}
        pending={remove.isPending}
        onCancel={() => setRemoving(null)}
        onConfirm={(entryId) => remove.mutate({ entryId })}
      />
    </ProfileSection>
  );
}
